import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatQuickReplyKey, ChatView } from '@brimo/shared';
import { errorMessage } from '../../../lib/api';
import {
  cancelChatAppointment,
  portalKeys,
  requestChatAppointment,
  sendChatMessage,
  uploadChatAttachment,
  usePortalChat,
} from '../api';

/**
 * The chat of the portal (Mandantenportal 22–26): the conversation (`GET /api/portal/chat`) and its actions. Every
 * action answers the whole `ChatView`, which replaces the cached one; bookings, cancellations and tickets also
 * refresh the start page and the ticket list (no SSE in the portal).
 */
export function useChat() {
  const queryClient = useQueryClient();
  const chat = usePortalChat();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback(
    async (view: ChatView, refreshOthers: boolean) => {
      queryClient.setQueryData(portalKeys.chat, view);
      if (refreshOthers) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: portalKeys.overview }),
          queryClient.invalidateQueries({ queryKey: portalKeys.tickets }),
        ]);
      }
    },
    [queryClient],
  );

  const run = useCallback(
    async (action: () => Promise<ChatView>, refreshOthers: boolean): Promise<boolean> => {
      setSending(true);
      setError(null);
      try {
        await apply(await action(), refreshOthers);
        return true;
      } catch (cause) {
        setError(errorMessage(cause));
        return false;
      } finally {
        setSending(false);
      }
    },
    [apply],
  );

  const sendText = useCallback((text: string) => run(() => sendChatMessage({ text }), true), [run]);
  const sendQuickReply = useCallback(
    (quickReply: ChatQuickReplyKey) => run(() => sendChatMessage({ quickReply }), true),
    [run],
  );
  const cancel = useCallback(
    (appointmentId: string) => run(() => cancelChatAppointment(appointmentId), true),
    [run],
  );
  const attach = useCallback(
    (file: File) =>
      run(async () => {
        const result = await uploadChatAttachment(file);
        return result.chat;
      }, false),
    [run],
  );

  /** „Termin verbindlich anfragen": errors (409 `SLOT_TAKEN` with fresh slots) go back to the slots card. */
  const book = useCallback(
    async (eventTypeId: string, startsAt: string) => {
      setError(null);
      await apply(await requestChatAppointment(eventTypeId, startsAt), true);
    },
    [apply],
  );

  return { chat, sending, error, setError, sendText, sendQuickReply, cancel, attach, book };
}

export type ChatController = ReturnType<typeof useChat>;
